<template>
  <component v-if="specialElement" :is="fieldType" :data="data" />
  <div v-else>
    <ion-item class="big-form-item" :lines="itemWithLine ? 'full' : 'none'">
      <ion-label v-if="data.label" position="stacked" text-wrap>{{ getLabel() }}</ion-label>
      <component :is="fieldType" :data="data" @valueChanged="handleChangedValue" />
    </ion-item>
    <div v-if="error" class="field-error"><ion-icon :icon="arrowUpOutline"></ion-icon> {{ error }}</div>
  </div>
</template>

<script lang="ts">
import {
  DBServiceAjaxData,
  DBServiceData,
  DBServiceField,
  DBServiceFieldArea,
  DBServiceFieldList,
} from "@/types"
import { IonItem, IonLabel, IonIcon } from "@ionic/vue"
import {
  defineComponent,
  defineAsyncComponent,
  ComputedRef,
  computed,
  ref,
  watch,
  toRef,
  onUpdated,
  Ref,
  onBeforeUnmount,
} from "vue"
import { arrowUpOutline } from "ionicons/icons"
import { useStore } from "vuex"

export default defineComponent({
  name: "Field",
  components: {
    IonIcon,
    IonItem,
    IonLabel,
    FieldText: defineAsyncComponent(
      () => import(/*webpackChunkName: "FieldText" */ "@/components/field_text.vue")
    ),
    FieldTextarea: defineAsyncComponent(
      () => import(/*webpackChunkName: "FieldTextarea" */ "@/components/field_textarea.vue")
    ),
    FieldSelect: defineAsyncComponent(
      () => import(/*webpackChunkName: "FieldSelect" */ "@/components/field_select.vue")
    ),
    FieldRadio: defineAsyncComponent(
      () => import(/*webpackChunkName: "FieldRadio" */ "@/components/field_radio.vue")
    ),
    FieldCheckbox: defineAsyncComponent(
      () => import(/*webpackChunkName: "FieldCheckbox" */ "@/components/field_checkbox.vue")
    ),
    Area: defineAsyncComponent(() => import(/*webpackChunkName: "Area" */ "@/components/area.vue")),
    List: defineAsyncComponent(() => import(/*webpackChunkName: "List" */ "@/components/list.vue")),
    Frame: defineAsyncComponent(() => import(/*webpackChunkName: "Frame" */ "@/components/frame.vue")),
  },
  props: {
    data: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  setup(props) {
    const store = useStore()
    const data = toRef(props, "data")
    const error = ref("")
    const itemWithLine = ref(true)
    const specialElement = ref(true)
    const abortController: Ref<AbortController | null> = ref(null)

    let ajaxData: DBServiceAjaxData | null = null

    const loadingDialogPresent: ComputedRef<boolean> = computed(() => {
      return store.getters["main/getLoadingDialogPresent"]
    })

    const getType = (type?: string, subtype?: string): string => {
      store.commit("main/setFullheight", false)
      itemWithLine.value = true
      specialElement.value = false

      if (subtype) {
        subtype = subtype.charAt(0).toUpperCase() + subtype.slice(1)
      }
      if (type === "string") {
        return `Field${subtype}`
      } else if (type === "lookup") {
        return `Field${subtype}`
      } else if (type === "checkbox") {
        type = type.charAt(0).toUpperCase() + type.slice(1)
        return `Field${type}`
      } else if (["area", "list", "frame"].includes(type as string)) {
        itemWithLine.value = false
        specialElement.value = true
        if (type === "frame" && data.value.fullheight) {
          store.commit("main/setFullheight", true)
        }
        if (type) {
          type = type.charAt(0).toUpperCase() + type.slice(1)
          return type
        }
      }
      return "FieldText"
    }

    const fieldType: ComputedRef<string | null> = computed(() => {
      if (props.data) {
        const data = props.data as DBServiceField
        return getType(data.type, data.subtype)
      }
      return getType()
    })

    const validateErrors: ComputedRef = computed(() => {
      return store.getters["main/getValidateErrors"]
    })

    const getLabel = (): string => {
      const data = props.data as DBServiceField
      if (data.label) {
        return data.label
      }
      return ""
    }

    const handleDataForChangedValue = async (ajax: DBServiceAjaxData) => {
      const url = ajax.url
      const postBody = store.getters["main/getPostBodyForRequest"]
      abortController.value = new AbortController()
      const resp: Response | void = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
        },
        body: postBody,
        signal: abortController.value.signal,
      }).catch((status) => {
        store.commit("main/setLoadingData")
        if (status.name !== "AbortError") {
          const message = `Not able to request AJAX(trigger) target: ${status}`
          throw new Error(message)
        }
      })
      if (resp && resp.ok) {
        const data = await resp.json()
        if ((data && data.url) || data.items) {
          await store.dispatch("main/updateItemsPerAjax", data)
        }
      } else if (resp) {
        store.commit("main/setLoadingData")
        const message = `${resp.status}, ${resp.statusText}`
        throw new Error(message)
      }
    }

    const handleChangedValue = async () => {
      ajaxData = null
      const field = data.value as DBServiceField | DBServiceFieldArea | DBServiceFieldList
      const ajax = field.ajax as DBServiceAjaxData | undefined
      if (ajax && ajax.trigger) {
        if (ajax.loadingmsg) {
          store.commit("main/setLoadingData", ajax.loadingmsg)
          ajaxData = ajax
        } else {
          if (abortController.value) {
            abortController.value.abort()
            if (ajax.loadingmsg) {
              store.commit("main/setLoadingData", false)
              ajaxData = null
            }
          }
          handleDataForChangedValue(ajax)
        }
      }
    }

    watch(loadingDialogPresent, async (ldPresent) => {
      if (ldPresent && ajaxData) {
        handleDataForChangedValue(ajaxData)
      }
    })

    watch(validateErrors, (validateErrors) => {
      const fieldName = data.value.name
      if (validateErrors && validateErrors[fieldName]) {
        error.value = validateErrors[fieldName].error
      } else {
        error.value = ""
      }
    })

    onUpdated(() => {
      const stepdata = store.getters["main/getStepData"] as DBServiceData
      const exScripts = stepdata.executescripts
      try {
        if (exScripts) {
          for (const sc of exScripts) {
            eval(sc)
          }
        }
      } catch (error) {
        throw new Error("Execute scripts Error:" + error)
      }
    })

    onBeforeUnmount(() => {
      if (abortController.value) {
        abortController.value.abort()
      }
    })

    return {
      itemWithLine,
      specialElement,
      getLabel,
      getType,
      fieldType,
      error,
      arrowUpOutline,
      handleChangedValue,
    }
  },
})
</script>

<style lang="scss" scoped>
.field-error {
  font-size: 1.1rem;
  text-align: left;
  color: var(--ion-color-danger);
  padding: 0.5rem 0 0.5rem;
}
</style>